<template>
    <vx-navbar
        v-if="!isMdShortHeight"
        :responsiveOffset="20"
        class="z-20! shrink-0 h-12! xl:h-14! bg-slate-100!"
        size="sm"
        color="light"
        :key="route.fullPath + summaryVersion"
    >
        <vx-navbar-action
            v-for="inbox in inboxes"
            :key="inbox.key"
            :data-test="`message-hub-navbar-${inbox.key}`"
            @click.prevent="handleInboxClick(inbox)"
            :href="inbox.href"
            :icon="inbox.ai ? undefined : inbox.icon"
            :active="inbox.active"
            hideArrow
            class="capitalize text-xs [&>span]:gap-1.5"
            :class="{ 'pr-1': !inbox.count }"
        >
            <vx-badge v-if="inbox.ai" size="xs" color="violet">
                <font-awesome-icon :icon="faSparkles" class="-ml-1"></font-awesome-icon>
                <span class="-mr-0.5">AI</span>
            </vx-badge>

            {{ inbox.key }}

            <vx-badge v-if="inbox.count" size="xs" color="primary">{{ inbox.count }}</vx-badge>
        </vx-navbar-action>

        <template v-slot:right>
            <router-button
                data-test="new-message-btn"
                size="sm"
                :to="{ name: 'message-hub.new-message', query: route.query }"
                >New Message <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon
            ></router-button>
        </template>
    </vx-navbar>
    <div
        v-else
        class="flex flex-row items-center justify-stretch h-12 rounded-2xl shadow bg-slate-50 mb-3 px-4 overflow-clip"
    >
        <router-link
            v-for="inbox in inboxes"
            :key="inbox.key"
            :to="inbox.to"
            class="flex flex-row items-center justify-center grow capitalize text-slate-800 hover:text-sky-600 duration-75 text-[11px] relative h-full"
        >
            <div>
                {{ inbox.key }}
            </div>
            <div v-if="inbox.count" class="ml-1 mb-2 w-1.5 h-1.5 rounded-full bg-sky-500"></div>
            <div v-if="inbox.active" class="absolute inset-be-0 inset-s-0 w-full h-1.5 bg-sky-500 rounded-t-md"></div>
        </router-link>
    </div>
</template>

<script setup>
import { faCirclePlus, faClock, faSparkles } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBadge, VxNavbar, VxNavbarAction } from '@voxie/frontend-components';
import { computed, inject, ref, watch, onUnmounted, onMounted } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import RouterButton from '~/components/general/RouterButton.vue';
import { canUseIntelligentInbox, getInboxIcon, isSameThread } from '~/components/message-hub/messageHubUtil';
import { useMessageHub } from '~/components/message-hub/useMessageHub';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { threadsClient } from '~/services';
import debounce from '~/utils/debounce';
import { catchRequestError } from '~/utils/request';
import { useResponsiveLayout } from '~/composables/useResponsiveLayout';
import { useConversation } from '~/composables/useConversation';
import { useScheduledMessages } from '~/components/message-hub/useScheduledMessages';

const { isMdShortHeight } = useResponsiveLayout();

const route = useRoute();
const router = useRouter();
const teamId = Spark.state.currentTeam.id;
const { onCorporate, getCurrentGroupId, getContext, currentOrgUnit } = useCurrentGroup();
const { currentInbox, isScheduledRoute, isInboxRoute, isIntelligentRoute } = useMessageHub();
const { activeThread } = useConversation();
const { fetchScheduledMessagesCount, scheduledMessagesCount } = useScheduledMessages();

const FEATURES = inject('FEATURES');

// used to store the previous visited thread for a specific inbox
// if i'm on the "open" inbox and visit the thread 555
// then go to the "all" inbox i wont see nothing because
// the "all" inbox didnt visit a thread yet
// if i go back to "open" the thread 555 will be available
const inboxesThreadHistory = ref({});

const isConversationRoute = (routeName) => {
    return ['message-hub.conversation', 'message-hub.contact-view'].includes(String(routeName));
};

const getInboxRoute = (inboxKey) => {
    const thread = inboxesThreadHistory.value[inboxKey];

    if (!thread) {
        return {
            name: 'message-hub',
            query: {
                inbox: inboxKey,
            },
        };
    }

    return {
        name: 'message-hub.conversation',
        params: {
            contactId: thread.contact_id,
        },
        query: {
            inbox: inboxKey,
            group: currentOrgUnit.value && thread.group_id ? thread.group_id : undefined,
        },
    };
};

const handleInboxClick = (inbox) => {
    if (
        inbox.key === 'intelligent' &&
        currentInbox.value === 'intelligent' &&
        activeThread.value &&
        isConversationRoute(route.name)
    ) {
        inboxesThreadHistory.value['intelligent'] = { ...activeThread.value };
    }
    router.push(inbox.to);
};

watch(currentInbox, (currentInbox, previousInbox) => {
    if (activeThread.value && currentInbox !== previousInbox) {
        inboxesThreadHistory.value[previousInbox] = { ...activeThread.value };
    }
});

const inboxes = computed(() => {
    const items = Object.keys(threadsSummary.value).map((key) => {
        const to = getInboxRoute(key);

        return {
            key: key,
            count: threadsSummary.value[key] > 999 ? '999+' : threadsSummary.value[key],
            icon: getInboxIcon(key),
            to: to,
            href: router.resolve(to).href,
            active: currentInbox.value === key && isInboxRoute.value,
        };
    });

    if (onCorporate() || getCurrentGroupId()) {
        items.push({
            key: 'scheduled',
            count: scheduledMessagesCount.value > 999 ? '999+' : scheduledMessagesCount.value,
            icon: faClock,
            to: { name: 'message-hub.scheduled-messages' },
            href: router.resolve({ name: 'message-hub.scheduled-messages' }).href,
            active: isScheduledRoute.value,
        });
    }

    if (canUseIntelligentInbox(FEATURES)) {
        const intelligentTo =
            currentInbox.value === 'intelligent'
                ? { name: 'message-hub', query: { inbox: 'intelligent' } }
                : getInboxRoute('intelligent');

        const intelligentItem = {
            key: 'intelligent',
            ai: true,
            count: 0,
            icon: faSparkles,
            to: intelligentTo,
            href: router.resolve(intelligentTo).href,
            active: isIntelligentRoute.value,
        };

        if (FEATURES.intelligent_inbox_last) {
            items.push(intelligentItem);
        } else {
            items.unshift(intelligentItem);
        }
    }

    return items;
});

// necessary to trigger the rerender function on the navbar component
const summaryVersion = ref(0);

const threadsSummary = ref(
    FEATURES.simple_message_hub
        ? { all: 0 }
        : {
              me: 0,
              unassigned: 0,
              open: 0,
              closed: 0,
              all: 0,
          }
);
let summaryAbort = new AbortController();
const fetchSummary = async () => {
    if (FEATURES.simple_message_hub) {
        return;
    }

    summaryAbort?.abort();
    summaryAbort = new AbortController();

    try {
        const context = getContext();
        const response = await threadsClient.getSummary(context, { signal: summaryAbort.signal });
        threadsSummary.value = {
            me: response.data.me || 0,
            unassigned: response.data.unassigned || 0,
            open: response.data.open || 0,
            closed: 0,
            all: 0,
        };

        summaryVersion.value++;
    } catch (error) {
        catchRequestError(error);
    }
};

watch(
    scheduledMessagesCount,
    () => {
        summaryVersion.value++;
    },
    {
        immediate: true,
    }
);
const debouncedFetchSummary = debounce(() => {
    fetchSummary();
    fetchScheduledMessagesCount({ teamId, groupId: getCurrentGroupId() });
}, 600);
watch(currentInbox, debouncedFetchSummary, {
    immediate: true,
});

// Handle thread updates for count refresh and cleanup stale inbox history
const handleThreadUpdated = ({ thread }) => {
    debouncedFetchSummary();

    if (thread) {
        cleanupThreadHistory(thread);
    }
};

const cleanupThreadHistory = (thread) => {
    const shouldCleanupInboxes = [];

    Object.keys(inboxesThreadHistory.value).forEach((inboxKey) => {
        const historyThread = inboxesThreadHistory.value[inboxKey];

        if (!historyThread) {
            return;
        }

        if (!isSameThread(historyThread, thread)) {
            return;
        }

        let shouldRemove = false;

        switch (inboxKey) {
            case 'closed':
                shouldRemove = thread.status !== 'closed';
                break;
            case 'open':
                shouldRemove = thread.status !== 'open';
                break;
            case 'unassigned':
                shouldRemove = thread.assignee_id !== null || thread.status !== 'open';
                break;
            case 'me':
                shouldRemove = thread.assignee_id !== Spark.state.user.id || thread.status !== 'open';
                break;
            case 'all':
            case 'intelligent':
                // 'all' and 'intelligent' inboxes should keep all threads, no cleanup needed
                break;
        }

        if (shouldRemove) {
            shouldCleanupInboxes.push(inboxKey);
        }
    });

    shouldCleanupInboxes.forEach((inboxKey) => {
        delete inboxesThreadHistory.value[inboxKey];
    });
};

let echoChannel;
onMounted(() => {
    const { teamId, orgUnitId, groupId } = getContext();
    const channelName =
        !orgUnitId && !groupId
            ? `messages.${teamId}`
            : orgUnitId
            ? `messages.${teamId}.org_unit.${orgUnitId}`
            : `messages.${teamId}.${groupId}`;
    echoChannel = window.Echo.private(channelName);

    echoChannel.listen('.ThreadUpdated', handleThreadUpdated);
});

onUnmounted(() => {
    echoChannel.stopListening('.ThreadUpdated', handleThreadUpdated);
});
</script>
